import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { X, Edit2, Check, Copy } from 'lucide-react';
import { DeviceTemplateProps, TraitStyleConfig, AttributeStyleConfig, IconStyleId } from '../../types';

// DeviceIcon import
import DeviceIcon from '../../../deviceIcons';

// Import Dynamic Trait Renderer
import { DynamicTrait, DynamicAttribute, getTraitStyleId, getAttributeStyleId } from '../../DynamicTraitRenderer';

// Import attribute components (fallback)
import { Style01_Card as SensorCard } from '../../attributes/SensorDisplay/Style01_Card';
import { Style02_Area as SensorAreaChart } from '../../attributes/SensorChart/Style02_Area';
import { Style03_DualAxis as TempHumidityChart } from '../../attributes/SensorChart/Style03_DualAxis';
import { Style01_Timeline as OccupancyTimeline } from '../../attributes/OccupancyTimeline';

/**
 * Sensor Template 01 - Dashboard Style
 * Multi-sensor dashboard with charts
 */
export const Template01_Dashboard: React.FC<DeviceTemplateProps> = ({
  device,
  onClose,
  onNicknameSave,
  config,
  thSensorChartData = [],
  isLoadingThSensorChart = false,
  luxChartData = [],
  isLoadingLuxChart = false,
  occupancyChartData = [],
  isLoadingOccupancyChart = false,
  isMerged,
  endpointCount,
  onToggleMerge
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState(device.nickname || device.deviceName);
  const blockSyncUntilRef = useRef<number>(0);
  const isSavingRef = useRef(false);

  // Extract styles from config
  const traitStyles: TraitStyleConfig = config?.traitStyles || {};
  const attributeStyles: AttributeStyleConfig = config?.attributeStyles || {};
  const smallIconStyle: IconStyleId = (config?.smallIconStyle || config?.iconStyle || 'Style01') as IconStyleId;
  const bigIconStyle: IconStyleId = (config?.bigIconStyle || config?.iconStyle || 'Style01') as IconStyleId;
  const hiddenComponents: string[] = config?.hiddenComponents || [];
  // Unified order: Attributes (BigIcon is always fixed at top)
  const traitOrder: string[] = config?.traitOrder || ['SensorDisplay', 'SensorChart', 'OccupancyTimeline'];

  // Sync tempName when device prop changes, but skip if we just saved
  useEffect(() => {
    if (!isEditingName && Date.now() > blockSyncUntilRef.current) {
      setTempName(device.nickname || device.deviceName);
    }
  }, [device.nickname, device.deviceName, isEditingName]);

  const handleSaveName = async () => {
    if (onNicknameSave && tempName.trim()) {
      isSavingRef.current = true;
      const savedName = tempName.trim();
      blockSyncUntilRef.current = Date.now() + 3000;
      await onNicknameSave(device.deviceId, savedName);
      setIsEditingName(false);
      isSavingRef.current = false;
    }
  };

  const handleBlur = () => {
    if (!isSavingRef.current) {
      setIsEditingName(false);
    }
  };

  // Check if we're in preview mode (deviceId starts with PREVIEW)
  const isPreviewMode = device.deviceId?.startsWith('PREVIEW') || false;
  
  // In preview mode, show all relevant sensors based on device type
  const hasTemperature = device.temperature !== undefined || (isPreviewMode && (device.deviceType === 'th_sensor' || device.deviceType === 'multi_sensor'));
  const hasHumidity = device.humidity !== undefined || (isPreviewMode && (device.deviceType === 'th_sensor' || device.deviceType === 'multi_sensor'));
  const hasLux = device.lux !== undefined || (isPreviewMode && (device.deviceType === 'lux_sensor' || device.deviceType === 'multi_sensor'));
  const hasOccupancy = device.occupancy !== undefined || device.presence !== undefined || (isPreviewMode && (device.deviceType === 'presence_sensor' || device.deviceType === 'motion_sensor' || device.deviceType === 'multi_sensor'));
  
  // Preview data for demonstration
  const previewTemperature = device.temperature ?? (isPreviewMode ? 25.5 : undefined);
  const previewHumidity = device.humidity ?? (isPreviewMode ? 65 : undefined);
  const previewLux = device.lux ?? (isPreviewMode ? 850 : undefined);
  const previewOccupancy = device.occupancy ?? device.presence ?? (isPreviewMode ? true : undefined);

  // Generate preview chart data for demonstration
  const generatePreviewData = (type: 'temperature' | 'humidity' | 'lux'): Array<{ time: string; value: number; temperature?: number; humidity?: number }> => {
    const now = new Date();
    const data = [];
    for (let i = 23; i >= 0; i--) {
      const time = new Date(now.getTime() - i * 60 * 60 * 1000);
      const hour = time.getHours();
      let value: number;
      if (type === 'temperature') {
        value = 24 + Math.sin(hour / 24 * Math.PI * 2) * 3 + (Math.random() - 0.5) * 2;
      } else if (type === 'humidity') {
        value = 60 + Math.cos(hour / 24 * Math.PI * 2) * 15 + (Math.random() - 0.5) * 5;
      } else {
        // lux - higher during day
        const dayFactor = hour >= 6 && hour <= 18 ? 1 : 0.1;
        value = 500 * dayFactor + Math.random() * 300 * dayFactor;
      }
      data.push({
        time: time.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' }),
        value: Math.round(value * 10) / 10,
        temperature: type === 'temperature' ? Math.round(value * 10) / 10 : undefined,
        humidity: type === 'humidity' ? Math.round(value) : undefined,
      });
    }
    return data;
  };

  // Determine sensor type for icon
  const getSensorType = (): 'presence_sensor' | 'lux_sensor' | 'th_sensor' => {
    if (hasOccupancy) return 'presence_sensor';
    if (hasLux) return 'lux_sensor';
    return 'th_sensor';
  };

  // Handle backdrop click - close modal or cancel editing
  const handleBackdropClick = useCallback(() => {
    if (isEditingName) {
      setIsEditingName(false);
      setTempName(device.nickname || device.deviceName);
    } else {
      onClose();
    }
  }, [isEditingName, device.nickname, device.deviceName, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4" onClick={handleBackdropClick}>
      <div className="bg-gradient-to-b from-[#0a1628] via-[#0d1d35] to-[#0a1628] rounded-3xl w-full max-w-sm max-h-[90vh] overflow-hidden shadow-2xl border border-slate-800/50" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <DeviceIcon 
              deviceType={getSensorType()} 
              isOn={true} 
              size={20}
              styleId={smallIconStyle}
              className="flex-shrink-0"
            />
            {isEditingName ? (
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <input
                  type="text"
                  value={tempName}
                  onChange={(e) => setTempName(e.target.value)}
                  className="flex-1 min-w-0 px-2 py-1 text-sm font-bold border border-slate-600 rounded bg-slate-800/80 text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveName();
                    }
                    if (e.key === 'Escape') setIsEditingName(false);
                  }}
                  onBlur={handleBlur}
                />
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleSaveName}
                  className="p-1 text-green-500 hover:text-green-400 flex-shrink-0"
                  title="儲存"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setIsEditingName(false)}
                  className="p-1 text-slate-400 hover:text-slate-300 flex-shrink-0"
                  title="取消"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-base font-bold text-white truncate">{device.nickname || device.deviceName}</h2>
                {onNicknameSave && (
                  <button 
                    onClick={() => {
                      setTempName(device.nickname || device.deviceName);
                      setIsEditingName(true);
                    }}
                    className="p-1 hover:bg-slate-700/50 rounded transition-colors flex-shrink-0"
                  >
                    <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                  </button>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <DynamicAttribute
              attributeName="StatusIndicator"
              styleId={getAttributeStyleId('StatusIndicator', attributeStyles)}
              componentProps={{
                online: device.status === 'online',
                label: device.status === 'online' ? 'ONLINE' : 'OFFLINE'
              }}
            />
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-slate-700/50 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 pb-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Big Icon - Visual representation of sensor (optional) */}
          {!hiddenComponents.includes('BigIcon') && (
            <div className="flex justify-center">
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-orange-500/20 to-blue-500/20 border-2 border-orange-500/30 flex items-center justify-center shadow-lg shadow-orange-500/20">
                <DeviceIcon 
                  deviceType={getSensorType()} 
                  isOn={true} 
                  size={64}
                  styleId={bigIconStyle}
                  className="text-orange-400"
                />
              </div>
            </div>
          )}

          {/* Dynamically rendered components based on traitOrder */}
          {traitOrder.map((componentName) => {
            if (hiddenComponents.includes(componentName)) return null;
            
            switch (componentName) {
              case 'SensorDisplay':
                const sensorCount = [hasTemperature, hasHumidity, hasLux, hasOccupancy].filter(Boolean).length;
                if (sensorCount === 0) return null;
                const isSingleSensor = sensorCount === 1;
                return (
                  <div key="SensorDisplay" className={isSingleSensor ? "flex justify-center" : "grid grid-cols-2 gap-3"}>
                    {hasTemperature && (
                      <DynamicAttribute
                        attributeName="SensorDisplay"
                        styleId={getAttributeStyleId('SensorDisplay', attributeStyles)}
                        componentProps={{ type: "temperature", value: previewTemperature, size: isSingleSensor ? "large" : "medium" }}
                      />
                    )}
                    {hasHumidity && (
                      <DynamicAttribute
                        attributeName="SensorDisplay"
                        styleId={getAttributeStyleId('SensorDisplay', attributeStyles)}
                        componentProps={{ type: "humidity", value: previewHumidity, size: isSingleSensor ? "large" : "medium" }}
                      />
                    )}
                    {hasLux && (
                      <DynamicAttribute
                        attributeName="SensorDisplay"
                        styleId={getAttributeStyleId('SensorDisplay', attributeStyles)}
                        componentProps={{ type: "lux", value: previewLux, size: isSingleSensor ? "large" : "medium" }}
                      />
                    )}
                    {hasOccupancy && (
                      <DynamicAttribute
                        attributeName="SensorDisplay"
                        styleId={getAttributeStyleId('SensorDisplay', attributeStyles)}
                        componentProps={{ type: "occupancy", value: previewOccupancy, size: isSingleSensor ? "large" : "medium" }}
                      />
                    )}
                  </div>
                );
              
              case 'SensorChart':
                // Render temp/humidity chart
                if (hasTemperature && hasHumidity && (thSensorChartData.length > 0 || isLoadingThSensorChart || isPreviewMode)) {
                  return (
                    <React.Fragment key="SensorChart">
                      <DynamicAttribute
                        attributeName="SensorChart"
                        styleId={getAttributeStyleId('SensorChart', attributeStyles)}
                        componentProps={{
                          data: thSensorChartData.length > 0 ? thSensorChartData : (isPreviewMode ? generatePreviewData('temperature') : []),
                          loading: isLoadingThSensorChart,
                          title: "過去 24 小時趨勢",
                          showTemperature: true,
                          showHumidity: true,
                          dataKey: "temperature",
                          color: "#f97316",
                          unit: "°C"
                        }}
                      />
                      {/* Lux Chart */}
                      {hasLux && (luxChartData.length > 0 || isLoadingLuxChart || isPreviewMode) && (
                        <DynamicAttribute
                          attributeName="SensorChart"
                          styleId={getAttributeStyleId('SensorChart', attributeStyles)}
                          componentProps={{
                            data: luxChartData.length > 0 ? luxChartData : (isPreviewMode ? generatePreviewData('lux') : []),
                            dataKey: "value",
                            loading: isLoadingLuxChart,
                            title: "過去 24 小時光照度趨勢",
                            color: "#eab308",
                            unit: " lux"
                          }}
                        />
                      )}
                    </React.Fragment>
                  );
                }
                // Single metric chart when only one temp/humidity is available
                if ((hasTemperature || hasHumidity) && (thSensorChartData.length > 0 || isLoadingThSensorChart || isPreviewMode)) {
                  return (
                    <React.Fragment key="SensorChart">
                      <DynamicAttribute
                        attributeName="SensorChart"
                        styleId={getAttributeStyleId('SensorChart', attributeStyles)}
                        componentProps={{
                          data: thSensorChartData.length > 0 ? thSensorChartData : (isPreviewMode ? generatePreviewData(hasTemperature ? 'temperature' : 'humidity') : []),
                          dataKey: hasTemperature ? 'temperature' : 'humidity',
                          loading: isLoadingThSensorChart,
                          title: "過去 24 小時趨勢",
                          color: hasTemperature ? '#f97316' : '#3b82f6',
                          unit: hasTemperature ? '°C' : '%'
                        }}
                      />
                      {/* Lux Chart */}
                      {hasLux && (luxChartData.length > 0 || isLoadingLuxChart || isPreviewMode) && (
                        <DynamicAttribute
                          attributeName="SensorChart"
                          styleId={getAttributeStyleId('SensorChart', attributeStyles)}
                          componentProps={{
                            data: luxChartData.length > 0 ? luxChartData : (isPreviewMode ? generatePreviewData('lux') : []),
                            dataKey: "value",
                            loading: isLoadingLuxChart,
                            title: "過去 24 小時光照度趨勢",
                            color: "#eab308",
                            unit: " lux"
                          }}
                        />
                      )}
                    </React.Fragment>
                  );
                }
                // Just Lux chart
                if (hasLux && (luxChartData.length > 0 || isLoadingLuxChart || isPreviewMode)) {
                  return (
                    <DynamicAttribute
                      key="SensorChart"
                      attributeName="SensorChart"
                      styleId={getAttributeStyleId('SensorChart', attributeStyles)}
                      componentProps={{
                        data: luxChartData.length > 0 ? luxChartData : (isPreviewMode ? generatePreviewData('lux') : []),
                        dataKey: "value",
                        loading: isLoadingLuxChart,
                        title: "過去 24 小時光照度趨勢",
                        color: "#eab308",
                        unit: " lux"
                      }}
                    />
                  );
                }
                return null;
              
              case 'OccupancyTimeline':
                if (!hasOccupancy || (occupancyChartData.length === 0 && !isLoadingOccupancyChart)) return null;
                return (
                  <DynamicAttribute
                    key="OccupancyTimeline"
                    attributeName="OccupancyTimeline"
                    styleId={getAttributeStyleId('OccupancyTimeline', attributeStyles)}
                    componentProps={{
                      data: occupancyChartData,
                      loading: isLoadingOccupancyChart,
                      title: "過去 24 小時佔用統計",
                      activeLabel: "有人",
                      inactiveLabel: "無人",
                      activeColor: "#22c55e",
                      inactiveColor: "#374151"
                    }}
                  />
                );
              
              default:
                return null;
            }
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-800/50 bg-slate-900/30 space-y-2">
          {/* Multi-endpoint device merge toggle - only for multi_channel devices */}
          {device.isMultiChannel && endpointCount && endpointCount > 1 && onToggleMerge && (
            <div className="flex items-center justify-between py-1 px-2 bg-slate-700/30 rounded-lg">
              <span className="text-xs text-slate-400">
                多端點設備 ({endpointCount} 個)
              </span>
              <button
                onClick={() => onToggleMerge(device.baseSN)}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  isMerged 
                    ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' 
                    : 'bg-slate-600/50 text-slate-300 hover:bg-slate-600'
                }`}
              >
                {isMerged ? '顯示為個別方塊' : '顯示為單一方塊'}
              </button>
            </div>
          )}
          
          {/* Location & SN with copy */}
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{device.location || device.groupName || ''}</span>
            <div className="flex items-center gap-1">
              <span className="font-mono">{device.deviceId || device.baseSN}</span>
              <button
                onClick={() => navigator.clipboard.writeText(device.deviceId || device.baseSN)}
                className="text-slate-400 hover:text-white transition-colors"
                title="複製設備 SN"
              >
                <Copy className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Template01_Dashboard;
